import { randomId } from '@mantine/hooks';
import { FC, useMemo, useState } from 'react';

import PriorityLabel from '@/components/AppointmentScheduler/PriorityLabel.tsx';
import ButtonSelection from '@/components/ButtonSelection.tsx';
import CircularLoader from '@/components/CircularLoader.tsx';

import { Appointment, Priority } from '@/models/appointment/Appointment.ts';

import { updateAppointment } from '@/services/appointmentService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

interface Props {
    value?: Priority;
    onChange?: (value: Priority) => void;
    // For immediate save mode (PatientModal)
    appointment?: Appointment;
    onSuccess?: (new_priority: Priority) => void;
}

const ImportanceSelector: FC<Props> = ({ value, onChange, appointment, onSuccess }) => {
    const [is_saving, setIsSaving] = useState(false);
    const form_id = useMemo(() => randomId(), []);

    const priority_options = Object.values(Priority).map((priority) => ({
        id: `${form_id}_${priority}`,
        value: priority,
        label: <PriorityLabel priority={priority} />,
    }));

    const handlePrioritySelect = async (selected_value: string): Promise<void> => {
        const new_priority = selected_value as Priority;

        // Form mode - just update the value
        if (!appointment) {
            onChange?.(new_priority);
            return;
        }

        // Immediate save mode - save to backend
        setIsSaving(true);
        const [success, message] = await updateAppointment(
            appointment,
            { appointment: { priority: new_priority } },
            'Fehler beim Aktualisieren der Priorität' // Error updating priority
        );

        if (success) {
            onSuccess?.(new_priority);
        } else {
            showErrorNotification(message);
        }
        setIsSaving(false);
    };

    if (is_saving) {
        return (
            <div className="flex items-center justify-center p-4">
                <CircularLoader />
            </div>
        );
    }

    return (
        <label htmlFor="priority" className="flex flex-col gap-1 text-sm font-bold">
            Wichtigkeit
            <ButtonSelection
                onSelect={handlePrioritySelect}
                options={priority_options}
                numberOfOptionsInRow={11}
                selected={value ? `${form_id}_${value}` : undefined}
                classNames={{ option: 'border-transparent !p-2' }}
            />
        </label>
    );
};

export default ImportanceSelector;
